import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  Directive, ElementRef,
  EmbeddedViewRef, EventEmitter,
  Injector,
  Input, OnChanges, OnDestroy, OnInit, Output, SimpleChange, SimpleChanges, TemplateRef
} from '@angular/core';
import {DsTooltipComponent} from "./ds-tooltip.component";
import {Utils} from "../../utils/utils";
import {IDsTooltip} from "./ds-tooltip.model";
import {fromEvent} from "rxjs/internal/observable/fromEvent";
import {defaultIfEmpty, first, takeUntil, tap} from "rxjs/operators";
import {Subject} from "rxjs";
import {merge} from "rxjs/internal/observable/merge";

@Directive({
  // tslint:disable-next-line:directive-selector
  selector: '[dsTooltip]',
  standalone: true,
})
export class DsTooltipDirective implements IDsTooltip, OnChanges, OnInit, OnDestroy {

  @Input('dsTooltip') content: TemplateRef<any> | string | number;
  @Input() dsTooltipMode: IDsTooltip['dsTooltipMode'] = 'hover';
  @Input() dsTooltipPosition: IDsTooltip['dsTooltipPosition'];
  @Input() dsTooltipAlign: IDsTooltip['dsTooltipAlign'];
  @Input() dsTooltipColor: IDsTooltip['dsTooltipColor'];
  @Input() dsTooltipSize: IDsTooltip['dsTooltipSize'] = 'medium';
  @Input() dsTooltipLeftPxs: IDsTooltip['dsTooltipLeftPxs'] = 0;
  @Input() dsTooltipTopPxs: IDsTooltip['dsTooltipTopPxs'] = 0;
  @Input() dsTooltipVisible: boolean = true;
  @Input() dsTooltipWithShadow: boolean = false;
  @Input() dsTooltipWithBorder: boolean = false;
  @Input() dsTooltipWithVideo: boolean = false;
  @Input() dsTooltipHoveredElement?: string;
  @Input() dsTooltipFont?: string;
  @Input() hostClass: IDsTooltip['dsTooltipHostIndex'] = 'cover-all';
  @Input() dsTooltipZIndex: IDsTooltip['dsTooltipZIndex'];
  @Output() onTooltipHide: EventEmitter<void> = new EventEmitter<void>();
  @Output() onTooltipClick: EventEmitter<void> = new EventEmitter<void>();
  closeAnimation: boolean;
  openAnimation: boolean;

  private _componentRef: ComponentRef<DsTooltipComponent>;
  private _hideTimer: any;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _element: ElementRef,
    private _applicationRef: ApplicationRef,
    private _injector: Injector,
    private _componentFactoryResolver: ComponentFactoryResolver
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes['content']) {
      const _changes: any = {};
      if (this.content instanceof TemplateRef) {
        _changes.template = new SimpleChange(this.content, changes['content'].currentValue, false);
      } else {
        _changes.content = new SimpleChange(this.content, changes['content'].currentValue, false);
      }
      this._componentRef && this._componentRef.instance.ngOnChanges(_changes);
      this.content = changes['content'].currentValue;
    }
  }

  ngOnInit() {
    this._setMode(this.dsTooltipMode);
  }

  private _setMode(mode: IDsTooltip['dsTooltipMode']): void {
    if (mode === 'click') {
      this._setClickMode();
      return;
    }

    if (mode === 'hoverStatic') {
      this._setHoverStaticMode();
      return;
    }

    if (mode === 'timer') {
      this._setTimerMode();
      return;
    }

    this._setHoverMode();
  }

  private _setTimerMode() {
    setTimeout(() => {
      this._showTooltip();
    });

    setTimeout(() => {
      this._hideTooltip();
      this._setHoverMode();
    }, 2000);
  }

  private _setHoverMode(): void {
    const eventTypes = ['mouseenter', 'mouseleave'];
    const events = eventTypes.map(e => fromEvent<Event>(this._element.nativeElement, e));
    const allEvents$ = merge(...events);
    allEvents$.pipe(
      defaultIfEmpty(null),
      takeUntil(this._destroyStream$)
    ).subscribe(e => {
      e && e.type === 'mouseenter' ? this._showTooltip() : this._hideTooltip();
    });
  }

  private _setHoverStaticMode(): void {
    const eventTypes = ['mouseenter', 'mouseleave'];
    const hoveredElement = this._element.nativeElement;
    const events = eventTypes.map(e => fromEvent<Event>(hoveredElement, e));
    const allEvents$ = merge(...events);
    allEvents$.pipe(
      defaultIfEmpty(null),
      takeUntil(this._destroyStream$)
    ).subscribe(e => {
      if (e && e.type === 'mouseenter') {
        hoveredElement.classList.add('hovered');
        this._hideTimer && clearTimeout(this._hideTimer);
        !this._componentRef && this._showTooltip();
        if (this.dsTooltipHoveredElement) {
          const closestElement = hoveredElement.closest(this.dsTooltipHoveredElement);
          closestElement && closestElement.classList.add('hovered');
        }
      } else {
        this._hideTimer = setTimeout(() => {
          this._hideTooltip();
        }, 0);
      }
    });
  }

  private _setClickMode(): void {
    fromEvent<Event>(this._element.nativeElement, 'click')
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(() => {
        !this._componentRef ? this._showTooltip() : null;
        this._componentRef && this._listenClickOutside();
      });
  }

  private _listenClickOutside(): void {
    fromEvent<Event>(document, 'click')
      .pipe(
        tap(e => {
          if (this._element?.nativeElement?.contains(e.target)) {
            this.onTooltipClick.emit();
          }
        }),
        defaultIfEmpty(null),
        takeUntil(this._destroyStream$),
        first(e => e && this._componentRef && !this._element.nativeElement.contains(e.target))
      )
      .subscribe(() => {
        this._hideTooltip();
      });
  }

  private _showTooltip(): void {
    if (this._componentRef || !this.content || !this.dsTooltipVisible) {
      return;
    }

    // create a tooltip component reference
    this._componentRef = this._componentFactoryResolver.resolveComponentFactory(DsTooltipComponent).create(this._injector);
    this._componentRef.instance.host = this._element.nativeElement;
    if (this.content instanceof TemplateRef) {
      this._componentRef.instance.template = this.content;
    } else {
      this._componentRef.instance.content = this.content;
    }
    this._componentRef.instance.position = this.dsTooltipPosition || 'bottom';
    this._componentRef.instance.align = this.dsTooltipAlign || 'center';
    this._componentRef.instance.color = this.dsTooltipColor || 'white';
    this._componentRef.instance.size = this.dsTooltipSize || 'medium';
    this._componentRef.instance.font = this.dsTooltipFont || 'regular';
    this._componentRef.instance.leftPxs = this.dsTooltipLeftPxs || 0;
    this._componentRef.instance.topPxs = this.dsTooltipTopPxs || 0;
    this._componentRef.instance.hostClass = this.hostClass;
    this._componentRef.instance.zIndex = this.dsTooltipZIndex;
    this._componentRef.instance.withShadow = Utils.isTruthy(this.dsTooltipWithShadow) || false;
    this._componentRef.instance.withBorder = Utils.isTruthy(this.dsTooltipWithBorder) || false;
    this._componentRef.instance.demoVideo = Utils.isTruthy(this.dsTooltipWithVideo) || false;
    this._componentRef.instance.mode = this.dsTooltipMode || 'hover';
    setTimeout(() => {
      this._componentRef.instance.openAnimation = true;
    })
    this.dsTooltipMode === 'hoverStatic' && this._componentRef.instance.mouseEvent.subscribe(e => {
      switch (e) {
        case 'mouseenter':
          clearTimeout(this._hideTimer);
          break;
        case 'mouseleave':
          this._hideTimer = setTimeout(() => this._hideTooltip(), 0);
          break;
      }
    });

    // attach component to the applicationRef so that it will be dirty checked.
    this._applicationRef.attachView(this._componentRef.hostView);
    const {nativeElement} = this._componentRef.location;
    // get DOM element from component
    document.body.appendChild(nativeElement);
    this._componentRef.hostView.detectChanges();
  }

  private _hideTooltip(): void {
    if (this._componentRef) {
      this._componentRef.instance.closeAnimation = true;
    }

    const listener = this._componentRef?.instance?.animationEnd.subscribe(a => {
      this._componentRef && this._componentRef.destroy();
      this._componentRef = null;
      listener.unsubscribe();
    })
    this.onTooltipHide.emit();
    if (this.dsTooltipHoveredElement) {
      const closestElement = this._element.nativeElement.closest(this.dsTooltipHoveredElement);
      closestElement && closestElement.classList.remove('hovered');
    }
    this._element.nativeElement.classList.remove('hovered');
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
    this._hideTooltip();
  }

}
